import React, { useState, useEffect, useRef } from 'react';
import { Volume2, VolumeX } from 'lucide-react';

const THEMES = [
  { name: 'normal', label: 'Default theme',       bg: '#0f172a', fg: '#f8fafc',  swatchBg: '#0f172a', swatchFg: '#22c55e'  },
  { name: 'dark',   label: 'Black & White theme', bg: '#000000', fg: '#ffffff',  swatchBg: '#000000', swatchFg: '#ffffff'  },
  { name: 'white',  label: 'White theme',         bg: '#f8fafc', fg: '#1e3a5f',  swatchBg: '#f8fafc', swatchFg: '#1e3a5f'  },
  { name: 'yellow', label: 'Yellow theme',        bg: '#fef9c3', fg: '#713f12',  swatchBg: '#fef08a', swatchFg: '#713f12'  },
  { name: 'sky',    label: 'Sky blue theme',      bg: '#e0f2fe', fg: '#0c4a6e',  swatchBg: '#bae6fd', swatchFg: '#0c4a6e'  },
  { name: 'orange', label: 'Orange theme',        bg: '#fff7ed', fg: '#431407',  swatchBg: '#fb923c', swatchFg: '#431407'  },
];

const BASE_FONT_SIZE = 16;
const MIN_FONT = 12;
const MAX_FONT = 22;
const STEP = 2;

const LS_THEME = 'a11y-theme';
const LS_FONT  = 'a11y-fontSize';

const hasTTS = typeof window !== 'undefined' && 'speechSynthesis' in window;

const AccessibilityBar = () => {
  const [activeTheme, setActiveTheme] = useState(() => localStorage.getItem(LS_THEME) || 'normal');
  const [fontSize, setFontSize] = useState(() => parseInt(localStorage.getItem(LS_FONT), 10) || BASE_FONT_SIZE);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const utteranceRef = useRef(null);

  // Apply theme on mount and when it changes
  useEffect(() => {
    const theme = THEMES.find(t => t.name === activeTheme) || THEMES[0];
    document.documentElement.style.setProperty('--a11y-bg', theme.bg);
    document.documentElement.style.setProperty('--a11y-fg', theme.fg);
    // Remove old theme classes and add current one
    THEMES.forEach(t => document.body.classList.remove('a11y-theme-' + t.name));
    document.body.classList.add('a11y-theme-' + activeTheme);
    localStorage.setItem(LS_THEME, activeTheme);

    const appEl = document.querySelector('.App');

    if (activeTheme === 'normal') {
      // Restore original site design — clear any forced inline styles so
      // Tailwind CSS classes take back control
      document.body.style.removeProperty('background-color');
      document.body.style.removeProperty('background');
      document.body.style.removeProperty('color');
      if (appEl) {
        appEl.style.removeProperty('background-color');
        appEl.style.removeProperty('background');
        appEl.style.removeProperty('color');
      }
    } else if (activeTheme === 'dark') {
      // Black & White: CSS !important rules in index.css handle all overrides.
      // Do NOT touch any element's inline styles here — doing so breaks React's
      // vDOM reconciliation and causes the swatch buttons to lose their colours
      // after switching away from this theme.
    } else {
      // Other colour themes — apply directly to .App
      if (appEl) {
        appEl.style.backgroundColor = theme.bg;
        appEl.style.color = theme.fg;
      }
    }
  }, [activeTheme]);

  // Apply font size on mount and when it changes
  useEffect(() => {
    document.documentElement.style.fontSize = fontSize + 'px';
    localStorage.setItem(LS_FONT, fontSize);
  }, [fontSize]);

  // Cancel speech when component unmounts
  useEffect(() => {
    return () => { if (hasTTS) window.speechSynthesis.cancel(); };
  }, []);

  const changeFontSize = (dir) => {
    if (dir === 0) {
      setFontSize(BASE_FONT_SIZE);
    } else {
      setFontSize(prev => Math.min(MAX_FONT, Math.max(MIN_FONT, prev + dir * STEP)));
    }
  };

  const toggleSpeech = () => {
    if (!hasTTS) return;

    if (isSpeaking) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
      return;
    }

    // Gather visible text from main content, falling back to full body
    const source =
      document.getElementById('main-content') ||
      document.querySelector('main') ||
      document.body;

    // Clone so we can strip the toolbar text without touching the DOM
    const clone = source.cloneNode(true);
    const toolbar = clone.querySelector('[data-a11y-toolbar]');
    if (toolbar) toolbar.remove();
    const text = clone.innerText || clone.textContent || '';

    if (!text.trim()) return;

    const utterance = new window.SpeechSynthesisUtterance(text.trim());
    utterance.lang = document.documentElement.lang || 'en-US';
    utterance.rate = 1;
    utterance.pitch = 1;

    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);

    utteranceRef.current = utterance;
    window.speechSynthesis.speak(utterance);
    setIsSpeaking(true);
  };

  return (
    <div
      role="toolbar"
      aria-label="Accessibility options"
      data-a11y-toolbar="true"
      className="fixed top-0 left-0 w-full z-[1000] bg-slate-800 border-b border-amber-500/25 px-4 flex items-center gap-1 flex-wrap"
      style={{ height: '36px', minHeight: '36px' }}
    >
      {/* Font size controls */}
      <button
        onClick={() => changeFontSize(1)}
        title="Increase font size"
        aria-label="Increase font size"
        className="inline-flex items-center justify-center bg-transparent border border-white/25 text-slate-200 rounded px-2 py-0.5 font-bold text-base leading-none cursor-pointer hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-amber-400 focus-visible:outline-offset-2 transition-colors"
        style={{ fontSize: '15px' }}
      >
        (A)+
      </button>
      <button
        onClick={() => changeFontSize(0)}
        title="Reset font size"
        aria-label="Reset font size"
        className="inline-flex items-center justify-center bg-transparent border border-white/25 text-slate-200 rounded px-2 py-0.5 font-bold leading-none cursor-pointer hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-amber-400 focus-visible:outline-offset-2 transition-colors"
        style={{ fontSize: '13px' }}
      >
        (A)
      </button>
      <button
        onClick={() => changeFontSize(-1)}
        title="Decrease font size"
        aria-label="Decrease font size"
        className="inline-flex items-center justify-center bg-transparent border border-white/25 text-slate-200 rounded px-2 py-0.5 font-bold leading-none cursor-pointer hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-amber-400 focus-visible:outline-offset-2 transition-colors"
        style={{ fontSize: '11px' }}
      >
        (A)-
      </button>

      {/* Divider */}
      <div aria-hidden="true" className="w-px h-5 bg-white/20 mx-1" />

      {/* Read aloud */}
      {hasTTS && (
        <button
          onClick={toggleSpeech}
          title={isSpeaking ? 'Stop reading' : 'Read page aloud'}
          aria-label={isSpeaking ? 'Stop reading' : 'Read page aloud'}
          aria-pressed={isSpeaking}
          className={`inline-flex items-center justify-center border rounded px-2 py-0.5 leading-none cursor-pointer focus-visible:outline-2 focus-visible:outline-amber-400 focus-visible:outline-offset-2 transition-colors ${
            isSpeaking
              ? 'bg-amber-500/20 border-amber-400 text-amber-300'
              : 'bg-transparent border-white/25 text-slate-200 hover:bg-white/10'
          }`}
          style={{ height: '24px' }}
        >
          {isSpeaking
            ? <VolumeX size={14} aria-hidden="true" />
            : <Volume2 size={14} aria-hidden="true" />}
        </button>
      )}

      {/* Divider */}
      <div aria-hidden="true" className="w-px h-5 bg-white/20 mx-1" />

      {/* Skip to main content */}
      <a
        href="#main-content"
        className="text-slate-400 text-xs no-underline px-2 py-0.5 rounded border border-transparent hover:text-slate-100 hover:border-white/20 focus-visible:outline-2 focus-visible:outline-amber-400 focus-visible:outline-offset-2 whitespace-nowrap transition-colors"
      >
        Skip to main content
      </a>

      {/* Colour swatches — pushed to right */}
      <div
        role="group"
        aria-label="Colour theme"
        className="flex items-center gap-1 ml-auto"
      >
        {THEMES.map((theme) => (
          <button
            key={theme.name}
            onClick={() => setActiveTheme(theme.name)}
            title={theme.label}
            aria-label={theme.label}
            aria-pressed={activeTheme === theme.name}
            data-swatch={theme.name}
            className="w-7 h-7 rounded flex items-center justify-center text-xs font-bold cursor-pointer transition-transform hover:scale-110 focus-visible:outline-2 focus-visible:outline-amber-400 focus-visible:outline-offset-2"
            style={{
              backgroundColor: theme.swatchBg,
              color: theme.swatchFg,
              border: activeTheme === theme.name
                ? '2px solid #f59e0b'
                : '2px solid rgba(255,255,255,0.3)',
              boxShadow: activeTheme === theme.name
                ? '0 0 0 2px rgba(245,158,11,0.4)'
                : 'none',
            }}
          >
            A
          </button>
        ))}
      </div>
    </div>
  );
};

export default AccessibilityBar;
